import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COURSES, courseById, MAX_BATCH_CAPACITY, DEFAULT_BATCH_CAPACITY, type BatchStatus } from "@mizpah/shared";
import { api, AdminError } from "../adminClient.js";
import { badgeClass } from "../badge.js";

interface Batch {
  batchId: string; courseId: string; name: string;
  startDate: string; endDate: string; capacity: number;
  status: BatchStatus; enrolledCount: number;
}

const blankForm = { courseId: COURSES[0].id as string, name: "", startDate: "", endDate: "", capacity: String(DEFAULT_BATCH_CAPACITY) };

export default function BatchesPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<Batch[]>([]);
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [showCompleted, setShowCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  function load() {
    setLoading(true);
    api<{ items: Batch[] }>("/admin/batches")
      .then((d) => setItems(d.items))
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load batches"))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  // Default view = active + upcoming only. The "Show completed" pill switches
  // to a completed-only view (not added to the active/upcoming list).
  const inCourse = (b: Batch) => courseFilter === "all" || b.courseId === courseFilter;
  const shown = useMemo(
    () => items.filter((b) => inCourse(b) && (showCompleted ? b.status === "Completed" : b.status !== "Completed")),
    [items, courseFilter, showCompleted]
  );
  const completedCount = useMemo(
    () => items.filter((b) => inCourse(b) && b.status === "Completed").length,
    [items, courseFilter]
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function createBatch(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setFormError("");
    try {
      await api("/admin/batches", {
        method: "POST",
        body: { ...form, capacity: Number(form.capacity) },
      });
      setForm(blankForm); setShowForm(false); load();
    } catch (e) {
      setFormError(e instanceof AdminError ? e.message : "Could not create the batch");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 4 }}>
        <h1>Batches</h1>
        <button className="rbtn coral" onClick={() => setShowForm((s) => !s)}>{showForm ? "Cancel" : "+ New batch"}</button>
      </div>
      <p className="subhead">Monthly enrollment groups. Click a batch to see its roster.</p>

      {showForm && (
        <form className="card pad" style={{ marginBottom: 18 }} onSubmit={createBatch}>
          {formError && <div className="login-err" style={{ marginBottom: 12 }}>{formError}</div>}
          <div className="g2form">
            <label className="fld"><span className="alabel">Program</span>
              <select className="ainput" value={form.courseId} onChange={set("courseId")}>
                {COURSES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="fld"><span className="alabel">Batch name</span>
              <input className="ainput" value={form.name} onChange={set("name")} placeholder="July 2026" />
            </label>
            <label className="fld"><span className="alabel">Start date</span>
              <input className="ainput" type="date" value={form.startDate} onChange={set("startDate")} />
            </label>
            <label className="fld"><span className="alabel">End date</span>
              <input className="ainput" type="date" value={form.endDate} onChange={set("endDate")} />
            </label>
            <label className="fld"><span className="alabel">Capacity</span>
              <input className="ainput" type="number" min={1} max={MAX_BATCH_CAPACITY} value={form.capacity} onChange={set("capacity")} aria-label="Capacity" />
            </label>
          </div>
          <div className="actions" style={{ marginTop: 14 }}>
            <button className="rbtn coral" type="submit" disabled={busy}>{busy ? "Creating..." : "Create batch"}</button>
          </div>
        </form>
      )}

      <div className="filters">
        <div className="filter-row">
          <div className="pills">
            <button
              className={`pill${showCompleted ? " on" : ""}`}
              onClick={() => setShowCompleted((s) => !s)}
              aria-pressed={showCompleted}
            >
              Show completed <span className="c">{completedCount}</span>
            </button>
          </div>
          <div className="selects">
            <select className="filter-select" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} aria-label="Filter by course">
              <option value="all">All programs</option>
              {COURSES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <p className="subhead">Loading...</p>}
      {error && <div className="login-err">{error}</div>}
      {!loading && !error && shown.length === 0 && (
        <div className="card empty">
          {showCompleted
            ? "No completed batches yet."
            : completedCount > 0
              ? `No active or upcoming batches. Tap "Show completed" to see ${completedCount} finished batch${completedCount === 1 ? "" : "es"}.`
              : "No batches yet. Create one to start enrolling."}
        </div>
      )}

      {shown.length > 0 && (
        <>
          <table>
            <thead><tr><th>Batch</th><th>Program</th><th>Dates</th><th>Enrolled</th><th>Status</th></tr></thead>
            <tbody>
              {shown.map((b) => (
                <tr key={b.batchId} onClick={() => nav(`/admin/batches/${b.batchId}`)}>
                  <td><div className="nm">{b.name}</div></td>
                  <td>{courseById(b.courseId)?.name ?? b.courseId}</td>
                  <td className="sub">{b.startDate} → {b.endDate}</td>
                  <td>{b.enrolledCount}/{b.capacity}</td>
                  <td><span className={badgeClass(b.status)}>{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="cards-list">
            {shown.map((b) => (
              <div key={b.batchId} className="card pad" style={{ marginBottom: 12, cursor: "pointer" }} onClick={() => nav(`/admin/batches/${b.batchId}`)}>
                <div className="row"><div className="nm">{b.name}</div><span className={badgeClass(b.status)}>{b.status}</span></div>
                <div className="sub" style={{ marginTop: 4 }}>{courseById(b.courseId)?.name} · {b.enrolledCount}/{b.capacity} enrolled · {b.startDate}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
